import { createSelector } from 'reselect';

import { DISCO_ADS_NAMESPACE } from '../../../../common/support/shareableReports';

import {
  DA_CAMPAIGNS_VIEW_NAMESPACE,
  DA_DEFAULT_DATE_RANGE,
  DA_INTENTS_AND_CREATIVES_VIEW_TABS,
  DA_NAMESPACE,
  DA_CAMPAIGN_CREATOR_NAMESPACE,
  DA_STEPPER_CARD_STEPS,
  DEFAULT_COUNTRY_TARGETING,
  INSTALL_CAMPAIGN
} from '../discoAdsConstants';

import {
  getRangeInTimezoneEndingNow,
  DEFAULT_DATE_FORMAT
} from '../../../support/dateUtils';

import { formatGeoEntitiesForSearchAndSelect } from '../discoAdsUtils';
// ---------------------- GENERAL SELECTORS ---------------------- //

const getDiscoAdsState = state => {
  return state[DA_NAMESPACE];
};

export const getAppName = state => {
  return state.app?.currentApp?.name || '';
};

const getDiscoAdsShareableState = state => {
  return state.shareable.data[DISCO_ADS_NAMESPACE] || {};
};

export const getCurrentView = createSelector(
  getDiscoAdsShareableState,
  discoAdsShareableState =>
    discoAdsShareableState.currentView || DA_CAMPAIGNS_VIEW_NAMESPACE
);

export const getDateRangeInfo = createSelector(
  getDiscoAdsShareableState,
  discoAdsShareableState => {
    const { dateRange, defaultRange } = discoAdsShareableState;

    if (dateRange) {
      return { dateRange, defaultRange: null };
    }

    if (defaultRange) {
      const dateRange = getRangeInTimezoneEndingNow(
        defaultRange,
        DEFAULT_DATE_FORMAT
      );
      return { dateRange, defaultRange };
    }

    const initDateRange = getRangeInTimezoneEndingNow(
      DA_DEFAULT_DATE_RANGE,
      DEFAULT_DATE_FORMAT
    );
    return { dateRange: initDateRange, defaultRange: DA_DEFAULT_DATE_RANGE };
  }
);

export const getActiveIntentsAndCreativesTab = createSelector(
  getDiscoAdsShareableState,
  discoAdsShareableState =>
    discoAdsShareableState.activeTabForIntentsAndCreatives ||
    DA_INTENTS_AND_CREATIVES_VIEW_TABS[0]
);

export const isPageLoading = createSelector(
  getDiscoAdsState,
  discoAdsState => discoAdsState.loading || false
);

// ----------------- CAMPAIGN MANAGEMENT SELECTORS ---------------------- //
export const getCampaigns = createSelector(
  getDiscoAdsState,
  discoAdsState => discoAdsState.campaigns.data || []
);

export const getCurrentCampaignIdFromShareable = createSelector(
  getDiscoAdsShareableState,
  discoAdsShareableState => discoAdsShareableState.currentCampaignId
);

export const getCurrentCampaign = createSelector(
  getDiscoAdsState,
  discoAdsState => discoAdsState.currentCampaign
);

export const getCurrentCampaignName = createSelector(
  getCurrentCampaign,
  currentCampaign => currentCampaign.name
);

export const getCurrentCampaignStatus = createSelector(
  getCurrentCampaign,
  currentCampaign => currentCampaign.status
);

export const getCurrentCampaignType = createSelector(
  getCurrentCampaign,
  currentCampaign => currentCampaign.type || INSTALL_CAMPAIGN // fallback on Install campaign
);

export const getCurrentCampaignTargetCountries = createSelector(
  getCurrentCampaign,
  currentCampaign => {
    const { geo_rules } = currentCampaign;
    if (geo_rules?.length) {
      return geo_rules.map(rule => rule.country_code);
    }
    // last resort in case geo_rules don't exist for a legacy campaign
    return DEFAULT_COUNTRY_TARGETING;
  }
);

export const getCurrentCampaignSettings = createSelector(
  getCurrentCampaign,
  currentCampaign => {
    const { id, name, total_budget, type, geo_rules } = currentCampaign;
    const campaignSettings = {
      id,
      name,
      budget: total_budget,
      objective: type,
      selectedGeoTargets: formatGeoEntitiesForSearchAndSelect(geo_rules)
    };
    return campaignSettings;
  }
);

// -------------- AD GROUP MANAGEMENT SELECTORS ---------------------- //
export const getAdGroups = createSelector(
  getDiscoAdsState,
  discoAdsState => discoAdsState.ad_groups.data || []
);

export const getCurrentAdGroup = createSelector(
  getDiscoAdsState,
  discoAdsState => discoAdsState.currentAdGroup
);

export const getCurrentAdGroupIdFromShareable = createSelector(
  getDiscoAdsShareableState,
  discoAdsShareableState => discoAdsShareableState.currentAdGroupId
);

export const getCurrentAdGroupName = createSelector(
  getCurrentAdGroup,
  currentAdGroup => currentAdGroup.name
);

export const getCurrentAdGroupStatus = createSelector(
  getCurrentAdGroup,
  currentAdGroup => currentAdGroup.status
);

// -------------------- INTENT MANAGEMENT SELECTORS -------------------- //

export const getIntentsForIntentsTable = createSelector(
  getDiscoAdsState,
  discoAdsState => discoAdsState.intents || []
);

// --------------------- CREATIVE MANAGEMENT SELECTORS ---------------- //

export const getCreativesForCreativesTable = createSelector(
  getDiscoAdsState,
  discoAdsState => discoAdsState.creatives || []
);

// ---------------------- CREATE FLOW SELECTORS ----------------------- //

export const getCreatorNamespace = createSelector(
  getDiscoAdsShareableState,
  discoAdsShareableState =>
    discoAdsShareableState.creatorNamespace || DA_CAMPAIGN_CREATOR_NAMESPACE
);

export const getCreatorStaticSteps = createSelector(
  getCreatorNamespace,
  namespace => {
    const campaignCreatorSteps = DA_STEPPER_CARD_STEPS;
    const adGroupCreatorSteps = DA_STEPPER_CARD_STEPS.slice(1);
    if (namespace === DA_CAMPAIGN_CREATOR_NAMESPACE) {
      return campaignCreatorSteps;
    }
    return adGroupCreatorSteps;
  }
);

export const getDiscoAdsCreatorData = createSelector(
  getDiscoAdsState,
  discoAdsState => discoAdsState.creator || {}
);

export const getCampaignSetup = createSelector(
  getDiscoAdsCreatorData,
  creator => creator.campaignSetup || {}
);

export const getGeoTargetingOptions = createSelector(
  getDiscoAdsState,
  discoAdsState => {
    const { geoTargetingShapes } = discoAdsState;
    return formatGeoEntitiesForSearchAndSelect(geoTargetingShapes);
  }
);

const getCampaignSetupTargetCountries = createSelector(
  getCampaignSetup,
  campaignSetup => {
    if (!campaignSetup?.selectedGeoTargets?.length) {
      return DEFAULT_COUNTRY_TARGETING;
    }
    const countryCodes = campaignSetup.selectedGeoTargets.map(
      geoEntity => geoEntity.countryCode
    );
    const uniqueContryCodes = [...new Set(countryCodes)];
    return uniqueContryCodes;
  }
);

export const getTargetCountriesForDiscoAdsIntents = createSelector(
  getCurrentCampaign,
  getCurrentCampaignTargetCountries,
  getCampaignSetupTargetCountries,
  (
    currentCampaign,
    currentCampaignTargetCountries,
    campaignSetupTargetCountries
  ) => {
    if (Object.keys(currentCampaign).length) {
      // If user accesses intents modal from anywhere but Create Campaign flow, then use geo-targeting from currentCampaign
      return currentCampaignTargetCountries;
    }
    return campaignSetupTargetCountries;
  }
);

const getCampaignSetupObjective = createSelector(
  getCampaignSetup,
  campaignSetup => campaignSetup.campaignObjective?.value
);

export const getCampaignObjectiveForCreativeSetup = createSelector(
  getCampaignSetupObjective,
  getCurrentCampaignType,
  (campaignSetupObjective, currentCampaignType) => {
    // the concept of campaign 'objective' is the same as campaign 'type'.
    // Backend Ads Data Service uses 'type'. Frontend uses 'objective'.
    if (campaignSetupObjective) {
      return campaignSetupObjective;
    }
    return currentCampaignType;
  }
);

export const getAdGroupSetup = createSelector(
  getDiscoAdsCreatorData,
  creator => creator.adGroupSetup || {}
);

export const getIntents = createSelector(
  getDiscoAdsCreatorData,
  creator => creator.intentsSetup?.intents || []
);

export const getDefaultBid = createSelector(
  getCurrentAdGroup,
  getAdGroupSetup,
  (currentAdGroup, adGroupSetup) => {
    if (currentAdGroup.default_max_cpc !== undefined) {
      const strBid = `$${currentAdGroup.default_max_cpc.toFixed(2)}`;
      return strBid;
    }
    return adGroupSetup.defaultBid;
  }
);

export const getIntentSearchResults = createSelector(
  getDiscoAdsState,
  discoAdsState => discoAdsState.intentSearchResults || []
);

export const getCreativeSetup = createSelector(
  getDiscoAdsCreatorData,
  creator => creator.creativeSetup || {}
);

export const getCreativeSetupImageUrl = createSelector(
  getCreativeSetup,
  creativeSetup => creativeSetup.imageUrl || null
);

export const getNewCampaignIdFromCreateCampaignFlow = createSelector(
  getCampaignSetup,
  campaignSetup => campaignSetup?.id
);

export const getNewAdGroupIdFromCreateAdGroupFlow = createSelector(
  getAdGroupSetup,
  adGroupSetup => adGroupSetup?.id
);

export const getAdGroupsNames = createSelector(
  getAdGroups,
  adGroups => adGroups.map(adGroup => adGroup.name)
);

export const getCampaignsNamesAndObjectives = createSelector(
  getCampaigns,
  campaignsData =>
    campaignsData.map(campaignData => ({
      name: campaignData.name,
      objective: campaignData.type
    }))
);
